import apiClient from './client';

import type { CanvasAssetType } from '@/types/canvas';
import type { MentionCandidateScope } from '@/types/mention';

/** 画布模式：分镜 / 资产 */
export type CanvasReferenceableMode = 'storyboard' | 'asset';

/**
 * 画布可引用资源扁平项（到具体 resourceId 粒度的业务实体）。
 * 字段对齐后端 `CanvasReferenceableResourceItem`（camelCase）。
 */
export interface CanvasReferenceableResourceItem {
  elementId: string;
  elementName: string;
  tagId?: string | null;
  tagLabel?: string | null;
  type: 'character' | 'location' | 'prop' | 'footage' | 'storyboard' | 'video' | 'sound' | 'voice';
  resourceId: string;
  resourceUrl?: string | null;
  thumbnailUrl?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  width?: number | null;
  height?: number | null;
  /** 视频/音频时长（秒） */
  duration?: number | null;
  resolution?: string | null;
  format?: string | null;
  frameRate?: number | null;
  version?: number | null;
  /** 方案/标签内版本序号（从1递增），用于拼接 `V{seq}` 显示名 */
  versionSeq?: number | null;
  isActive?: boolean | null;
  /** 人像资产类型：1=真人形象，2=虚拟人像 */
  humanAssetType?: number | null;
  /** 人像资产状态：0=处理中，1=已激活，2=失败 */
  humanAssetStatus?: number | null;
  /** 提交身份类型：1=资源图 2=分镜图片 3=分镜视频 5=音效 6=配音 */
  resourceType: 1 | 2 | 3 | 5 | 6;
}

export interface CanvasReferenceableResourcesResponse {
  resources: CanvasReferenceableResourceItem[];
}

export interface CanvasReferenceableResourcesQuery {
  projectId: string;
  mode: CanvasReferenceableMode;
  storyboardId?: string | null;
  assetId?: string | null;
  assetType?: CanvasAssetType | string | null;
  /** 生成类型硬过滤（与编辑页 mention-candidates 同规则） */
  genType: 'image' | 'video' | 'sound' | 'voice' | 'text';
  /** genType=video 时必填 */
  videoMode?: string | null;
  /** 缺省按 @ 面板候选解析；帧 picker 传 'frame' */
  scope?: MentionCandidateScope | null;
}

export const canvasReferenceableResourcesApi = {
  async list(
    params: CanvasReferenceableResourcesQuery
  ): Promise<CanvasReferenceableResourcesResponse> {
    const response = (await apiClient.get(
      `/projects/${params.projectId}/canvas/referenceable-resources`,
      {
        params: {
          mode: params.mode,
          genType: params.genType,
          videoMode: params.videoMode ?? undefined,
          storyboardId: params.storyboardId ?? undefined,
          assetId: params.assetId ?? undefined,
          assetType: params.assetType ?? undefined,
          scope: params.scope ?? undefined,
        },
      }
    )) as unknown as CanvasReferenceableResourcesResponse;

    return { resources: response.resources ?? [] };
  },
};
